// Localhost Verify fidelity: ship same-origin image + font bytes in-band.
//
// Cloud archivers cannot fetch `http://localhost:...` (SSRF + wrong host).
// During onboarding Verify the customer's app is often on loopback, so the
// live rrweb preview shows broken images / Times fallbacks even though the
// browser already has the bytes. Read cache-first from the page, emit a
// custom event, let the Verify player rewrite URLs to data: URLs.
//
// Strictly loopback-only. Public HTTPS origins keep using server-side asset
// archival — no COGS hit here. Never throws into the recording path.

export const LOCAL_ASSET_TAG = 'hb-local-asset';

export interface LocalAssetPayload {
    href: string;
    contentType: string;
    bodyBase64: string;
}

const MAX_ASSET_BYTES = 400_000;
const MAX_TOTAL_BYTES = 2_000_000;
// A couple heroes + next/font woff2 files (Inter / display).
const MAX_ASSETS = 12;
const RESCAN_DELAY_MS = 2_000;

const CSS_URL_RE = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;

type FetchLike = (
    input: string,
    init?: RequestInit,
) => Promise<{
    ok: boolean;
    headers: { get(name: string): string | null };
    arrayBuffer(): Promise<ArrayBuffer>;
}>;

export function isLocalVerifyOrigin(href: string): boolean {
    try {
        const u = new URL(href);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
        const h = u.hostname.replace(/^\[|\]$/g, '').toLowerCase();
        return h === 'localhost' || h.endsWith('.localhost') || h === '127.0.0.1' || h === '::1';
    } catch {
        return false;
    }
}

function guessContentType(href: string, header: string | null): string {
    const fromHeader = (header ?? '').split(';')[0].trim().toLowerCase();
    if (fromHeader.startsWith('image/') || fromHeader.startsWith('font/')) return fromHeader;
    if (fromHeader === 'application/font-woff' || fromHeader === 'application/font-woff2') {
        return fromHeader.includes('woff2') ? 'font/woff2' : 'font/woff';
    }
    if (/\.svg(\?|#|$)/i.test(href)) return 'image/svg+xml';
    if (/\.png(\?|#|$)/i.test(href)) return 'image/png';
    if (/\.(jpe?g)(\?|#|$)/i.test(href)) return 'image/jpeg';
    if (/\.gif(\?|#|$)/i.test(href)) return 'image/gif';
    if (/\.webp(\?|#|$)/i.test(href)) return 'image/webp';
    if (/\.avif(\?|#|$)/i.test(href)) return 'image/avif';
    if (/\.woff2(\?|#|$)/i.test(href)) return 'font/woff2';
    if (/\.woff(\?|#|$)/i.test(href)) return 'font/woff';
    if (/\.ttf(\?|#|$)/i.test(href)) return 'font/ttf';
    if (/\.otf(\?|#|$)/i.test(href)) return 'font/otf';
    return fromHeader || 'application/octet-stream';
}

function isAllowedContentType(contentType: string, href: string): boolean {
    if (contentType.startsWith('image/') || contentType.startsWith('font/')) return true;
    // Some Next/static hosts serve woff2 as octet-stream.
    if (
        contentType === 'application/octet-stream' &&
        /\.(woff2?|ttf|otf)(\?|#|$)/i.test(href)
    ) {
        return true;
    }
    return false;
}

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
}

/** Collapse Next.js `/_next/image?url=...&w=N` variants to one key per source url. */
function assetDedupeKey(absHref: string): string {
    try {
        const u = new URL(absHref);
        if (u.pathname.includes('/_next/image')) {
            const inner = u.searchParams.get('url');
            if (inner) return `next:${inner}`;
        }
        u.searchParams.delete('w');
        u.searchParams.delete('q');
        u.searchParams.delete('width');
        return u.origin + u.pathname + (u.searchParams.toString() ? `?${u.searchParams}` : '');
    } catch {
        return absHref;
    }
}

function collectCssUrls(
    cssText: string,
    cssBaseHref: string,
    add: (raw: string, baseHref: string) => void,
): void {
    CSS_URL_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = CSS_URL_RE.exec(cssText))) {
        const raw = (match[2] || '').trim();
        if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) continue;
        // Fonts (and the occasional css-referenced image).
        if (
            /\.(woff2?|ttf|otf|eot|png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i.test(raw) ||
            raw.includes('/_next/static/media/') ||
            raw.includes('/media/')
        ) {
            // Resolve relative to the stylesheet URL, not the page path —
            // `url(../media/x.woff2)` from `/_next/static/css/...` must become
            // `/_next/static/media/x.woff2`, not `/courses/media/...`.
            add(raw, cssBaseHref);
        }
    }
}

// Image + font URLs currently referenced (absolute). Prefer currentSrc for
// images; pull @font-face / next/font files from stylesheets + preload links.
export function localAssetHrefs(doc: Document, pageHref: string): string[] {
    if (!isLocalVerifyOrigin(pageHref)) return [];
    const out: string[] = [];
    const seenKey = new Set<string>();
    const add = (raw: string | null | undefined, baseHref: string = pageHref) => {
        if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) return;
        let abs: string;
        try {
            abs = new URL(raw, baseHref).href;
        } catch {
            return;
        }
        if (!isLocalVerifyOrigin(abs)) return;
        const key = assetDedupeKey(abs);
        if (seenKey.has(key)) return;
        seenKey.add(key);
        out.push(abs);
    };

    try {
        const imgs = doc.querySelectorAll('img');
        for (let i = 0; i < imgs.length; i++) {
            const el = imgs[i] as HTMLImageElement;
            add(el.currentSrc || el.getAttribute('src'));
        }
        const others = doc.querySelectorAll(
            'video[poster], image[href], image[xlink\\:href], link[rel="preload"][as="font"]',
        );
        for (let i = 0; i < others.length; i++) {
            const el = others[i];
            add(el.getAttribute('poster'));
            add(el.getAttribute('href') || el.getAttribute('xlink:href'));
        }

        // @font-face src from readable stylesheets (next/font self-host).
        const sheets = doc.styleSheets;
        for (let i = 0; i < sheets.length; i++) {
            const sheet = sheets[i] as CSSStyleSheet;
            const sheetBase = sheet.href || pageHref;
            let rules: CSSRuleList;
            try {
                rules = sheet.cssRules;
            } catch {
                // Opaque cross-origin sheet — skip (loopback same-origin is fine).
                if (sheet.href) add(sheet.href, pageHref);
                continue;
            }
            for (let j = 0; j < rules.length; j++) {
                const text = (rules[j] as CSSRule).cssText || '';
                // Duck-type: jsdom often lacks CSSFontFaceRule. collectCssUrls
                // already filters to font/image-looking urls.
                if (text.includes('url(')) collectCssUrls(text, sheetBase, add);
            }
        }

        // Inline <style> blocks (next/font often injects here).
        // Prefer document URL; next/font urls are usually root-absolute.
        const styles = doc.querySelectorAll('style');
        for (let i = 0; i < styles.length; i++) {
            const text = styles[i].textContent;
            if (text) collectCssUrls(text, pageHref, add);
        }
    } catch {
        return out;
    }
    return out;
}

/** @deprecated use localAssetHrefs */
export function localImageHrefs(doc: Document, pageHref: string): string[] {
    return localAssetHrefs(doc, pageHref);
}

export class LocalAssetSnapshotCapture {
    private captured = new Set<string>();
    private bytesSent = 0;
    private count = 0;
    private rescanTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private emit: (payload: LocalAssetPayload) => void,
        private fetchImpl?: FetchLike,
        private encode: (bytes: Uint8Array) => string = bytesToBase64,
    ) {}

    captureFromDocument(doc: Document, pageHref: string): void {
        try {
            if (!isLocalVerifyOrigin(pageHref)) return;
            void this.capture(localAssetHrefs(doc, pageHref));
            if (this.rescanTimer) clearTimeout(this.rescanTimer);
            this.rescanTimer = setTimeout(() => {
                this.rescanTimer = null;
                try {
                    void this.capture(localAssetHrefs(doc, pageHref));
                } catch {
                    /* best-effort */
                }
            }, RESCAN_DELAY_MS);
        } catch {
            /* never throw into the recording path */
        }
    }

    async capture(hrefs: string[]): Promise<void> {
        const fetcher: FetchLike | undefined =
            this.fetchImpl ?? (typeof fetch !== 'undefined' ? (fetch.bind(globalThis) as FetchLike) : undefined);
        if (!fetcher) return;

        for (const href of hrefs) {
            if (this.captured.has(href)) continue;
            if (this.count >= MAX_ASSETS) return;
            if (this.bytesSent >= MAX_TOTAL_BYTES) return;
            this.captured.add(href);
            try {
                const res = await fetcher(href, {
                    mode: 'cors',
                    credentials: 'omit',
                    cache: 'force-cache',
                });
                if (!res.ok) continue;
                const buf = new Uint8Array(await res.arrayBuffer());
                if (!buf.length || buf.length > MAX_ASSET_BYTES) continue;
                if (this.bytesSent + buf.length > MAX_TOTAL_BYTES) continue;
                let contentType = guessContentType(href, res.headers.get('content-type'));
                if (!isAllowedContentType(contentType, href)) continue;
                if (contentType === 'application/octet-stream') {
                    contentType = guessContentType(href, null);
                }
                const bodyBase64 = this.encode(buf);
                this.bytesSent += buf.length;
                this.count++;
                this.emit({ href, contentType, bodyBase64 });
            } catch {
                /* CORS / network: leave gap */
            }
        }
    }

    dispose(): void {
        if (this.rescanTimer) clearTimeout(this.rescanTimer);
        this.rescanTimer = null;
    }
}
